<template>
  <div>
    <b-sidebar
      id="add-new-patient-sidebar"
      :visible="isAddNewPatientSidebarActive"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      shadow
      backdrop
      no-header
      right
      @hidden="resetForm"
      @change="val => $emit('update:is-add-new-patient-sidebar-active', val)">
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">{{ $t('Add') }} {{ $t('NewV2') }} {{ $t('Patient') }}</h5>
          <i class="fas fa-times ml-1 cursor-pointer" size="16" @click="hide"></i>
        </div>

        <h6 class="m-6 text-danger">
          All data will be reviewed and should be completed with true information.
        </h6>

        <!-- BODY -->
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm">
            <!-- First Name -->
            <validation-provider
              #default="validationContext"
              name="First Name"
              rules="required">
              <b-form-group :label="$t('Form.Name')" label-for="name">
                <b-form-input
                  id="name"
                  v-model="stateData.name"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="First Name" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Last Name -->
            <validation-provider
              #default="validationContext"
              name="Last Name"
              rules="required">
              <b-form-group :label="$t('Form.Last Name')" label-for="lastname">
                <b-form-input
                  id="lastname"
                  v-model="stateData.lastname"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Last Name" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Email -->
            <validation-provider
              name="Email"
              rules="required|email"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Email"
                label-for="email"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Email is required and must be valid.">
                <b-form-input
                  id="email"
                  v-model="stateData.email"
                  :class="classes"
                  placeholder="Enter email"></b-form-input>
              </b-form-group>
            </validation-provider>

            <!-- Phone -->
            <validation-provider
              #default="validationContext"
              name="Phone"
              rules="required|max:14">
              <b-form-group :label="$t('Form.Phone')" label-for="contact">
                <b-form-input
                  id="contact"
                  v-model="formatPhone"
                  :state="getValidationState(validationContext)" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Gender -->
            <validation-provider name="Gender" rules="required">
              <b-form-group :label="$t('Form.Gender')" label-for="gender">
                <treeselect
                  id="gender"
                  v-model="stateData.gender"
                  :options="genderOptions"
                  :normalizer="normalizer" />
                <b-form-invalid-feedback>
                  {{ $t('This field is required') }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Type visit -->
            <validation-provider
              #default="validationContext"
              name="Type visit"
              rules="required">
              <b-form-group label-for="type_visit">
                <label for="type_visit">{{ $t('Type visit') }}</label>
                <treeselect
                  id="type_visit"
                  v-model="stateData.type_visits_id"
                  :state="getValidationState(validationContext)"
                  :options="optionsTypeVisit"
                  :normalizer="normalizer" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Conditional Content -->
            <div v-if="showConditionalContent">
              <membership @dataMemberchip="handleDataMemberchip" />
            </div>

            <!-- Visit Reason -->
            <validation-provider
              #default="validationContext"
              name="Visit Reason"
              rules="required">
              <b-form-group label-for="visit_reason">
                <label for="visit_reason">{{ $t('Visit Reason') }}</label>
                <treeselect
                  id="visit_reason"
                  v-model="stateData.reason_visit"
                  :options="visitReasonOptions"
                  :normalizer="normalizer"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  placeholder="Select a visit reason"
                  :max-height="200"
                  :options-limit="50"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': validationContext.touched && !validationContext.valid
                  }" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Other Reason Input (conditional) -->
            <validation-provider
              v-if="showOtherReasonInput"
              #default="validationContext"
              name="Other Reason"
              rules="required">
              <b-form-group label-for="reason_visit_other">
                <label for="reason_visit_other">{{ $t('Please specify') }}</label>
                <b-form-textarea
                  id="reason_visit_other"
                  v-model="stateData.reason_visit_other"
                  :state="getValidationState(validationContext)"
                  placeholder="Please specify the reason for your visit"
                  rows="3" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Address -->
            <ValidationProvider
              name="Address"
              rules="required"
              v-slot="{ errors, classes }">
              <b-form-group
                label="Address"
                label-for="address"
                :state="errors.length === 0 ? null : false"
                invalid-feedback="Address is required.">
                <b-form-input
                  id="address"
                  v-model="stateData.address"
                  :class="classes"
                  placeholder="Enter address"></b-form-input>
              </b-form-group>
            </ValidationProvider>

            <!-- City: input + lista automática (debe seleccionar de la lista con clic) -->
            <validation-provider
              #default="validationContext"
              name="City"
              rules="required">
              <b-form-group
                label-for="city"
                class="city-autocomplete-wrap">
                <template #label>
                  {{ $t('Form.City') }} <span class="text-danger">*</span>
                  <small class="text-muted">({{ $t('Required') }})</small>
                </template>
                <small class="d-block text-info mb-1">
                  <i class="fas fa-info-circle mr-1"></i>
                  Escriba el nombre de la ciudad y luego <strong>debe hacer clic en una opción de la lista</strong> para seleccionarla.
                </small>
                <b-form-input
                  id="city"
                  v-model="cityName"
                  :state="stateData.cities_id ? true : (cityName ? false : null)"
                  placeholder="Escriba y seleccione de la lista desplegable"
                  @input="searchCitiesFromInput"
                  @focus="cityName && searchCitiesFromInput()"
                  @blur="validationContext.validate" />
                <div v-if="cityOptions.length" class="city-options-list">
                  <div
                    v-for="opt in cityOptions"
                    :key="opt.value"
                    class="city-option-item"
                    @mousedown.prevent="selectCityOption(opt)">
                    <i class="fas fa-mouse-pointer mr-1 text-muted small"></i>{{ opt.text }}
                  </div>
                </div>
                <b-form-invalid-feedback v-if="cityName && !stateData.cities_id">
                  Debe seleccionar una ciudad de la lista haciendo clic en ella.
                </b-form-invalid-feedback>
                <b-form-invalid-feedback v-else>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Zip Code -->
            <validation-provider
              #default="validationContext"
              name="Zip Code"
              rules="required">
              <b-form-group label="Zip Code" label-for="zip-code">
                <b-form-input
                  id="zip-code"
                  v-model="stateData.zip_codes_id"
                  :state="getValidationState(validationContext)"
                  type="number"
                  placeholder="Enter zip code" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Reference -->
            <validation-provider
              v-if="showConditionalContentRef"
              v-slot="{ errors, classes, valid, invalid }"
              name="Reference"
              rules="required">
              <b-form-group
                label-for="reference"
                :state="errors.length === 0 ? null : false">
                <label for="reference">{{ $t('Reference') }}</label>
                <treeselect
                  id="reference"
                  v-model="stateData.user_references_id"
                  :options="optionsReferenceList"
                  :normalizer="normalizer"
                  :class="{ 'is-invalid': errors.length }"
                  placeholder="Select reference" />
                <!-- Mostrar mensaje de error -->
                <b-form-invalid-feedback v-if="errors.length">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Date of Birth -->
            <validation-provider #default="validationContext" name="DOB" rules="required">
              <b-form-group label="DOB" label-for="date-birth">
                <flat-pickr
                  v-model="stateData.date_birth"
                  :config="config"
                  class="form-control"
                  :state="getValidationState(validationContext)"
                  placeholder="MM-DD-YYYY"
                  :class="{
                    'is-invalid': validationContext.touched && !validationContext.valid,
                  }" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Cupon -->
            <checkCupon :cupon.sync="stateData.cupon" @cancelCupon="cancelCupon" />

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button variant="primary" class="mr-2" type="submit" :disabled="loading">
                <span v-if="!loading">{{ $t('Add') }}</span>
                <span v-else><SpinnerLoading /></span>
              </b-button>
              <b-button type="button" variant="outline-danger" @click="hide">{{
                $t('Cancel')
              }}</b-button>
            </div>
          </b-form>
        </validation-observer>
      </template>
    </b-sidebar>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormTextarea,
  BFormSelect,
} from 'bootstrap-vue'
import formValidation from '@/core/utils/forms/form-validation'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import SpinnerLoading from '@/components/SpinnerLoading'
import flatPickr from 'vue-flatpickr-component'
import { required } from '@validations'
import { computed, onMounted, ref, watch } from '@vue/composition-api'
import ToastNotification from '@/components/ToastNotification'
import checkCupon from '@/components/checkCupon'
import membership from './MemberchipAdd.vue'
import { notificationCheck } from '@/mixins/NotificationSetup'
import axiosP from '@/core/services/api/patient'
import axiosC from '@/core/services/api/admin/cities'
import axiosTV from '@/core/services/api/admin/consults/visits'
import axiosA from '@/core/services/api/appointment/actions'

export default {
  components: {
    BFormTextarea,
    BFormSelect,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    ToastNotification,
    BButton,
    SpinnerLoading,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    checkCupon,
    membership,
  },
  model: {
    prop: 'isAddNewPatientSidebarActive',
    event: 'update:is-add-new-patient-sidebar-active',
  },
  props: {
    isAddNewPatientSidebarActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      genderOptions: [
        { name: 'Male', id: 'male' },
        { name: 'Female', id: 'female' },
      ],
      visitReasonOptions: [
        { id: 'Other', name: 'Other' },
        { id: 'Abdominal Pain', name: 'Abdominal Pain' },
        { id: 'Allergies', name: 'Allergies' },
        { id: 'Anxiety', name: 'Anxiety' },
        { id: 'Back Pain', name: 'Back Pain' },
        { id: 'Breast Pain', name: 'Breast Pain' },
        { id: 'Chest Pain', name: 'Chest Pain' },
        { id: 'Cold Symptoms', name: 'Cold Symptoms' },
        { id: 'Consultation', name: 'Consultation' },
        { id: 'Cough', name: 'Cough' },
        { id: 'COVID', name: 'COVID' },
        { id: 'Dermatitis', name: 'Dermatitis' },
        { id: 'Diabetes', name: 'Diabetes' },
        { id: 'Diarrhea', name: 'Diarrhea' },
        { id: 'DIU Removal', name: 'DIU Removal' },
        { id: 'Dizziness', name: 'Dizziness' },
        { id: 'DM Control', name: 'DM Control' },
        { id: 'Earache', name: 'Earache' },
        { id: 'Exams', name: 'Exams' },
        { id: 'Fever', name: 'Fever' },
        { id: 'Flu', name: 'Flu' },
        { id: 'Follow Up', name: 'Follow Up' },
        { id: 'General Check', name: 'General Check' },
        { id: 'General Exam', name: 'General Exam' },
        { id: 'Headache', name: 'Headache' },
        { id: 'Health Checkup', name: 'Health Checkup' },
        { id: 'HTN Control', name: 'HTN Control' },
        { id: 'Ingrown Toe Nail', name: 'Ingrown Toe Nail' },
        { id: 'Injection', name: 'Injection' },
        { id: 'IV Therapy', name: 'IV Therapy' },
        { id: 'Knee Pain', name: 'Knee Pain' },
        { id: 'Lab Result', name: 'Lab Result' },
        { id: 'Lower Back Pain', name: 'Lower Back Pain' },
        { id: 'Menstrual Disorder', name: 'Menstrual Disorder' },
        { id: 'Pending Injection', name: 'Pending Injection' },
        { id: 'Physical School', name: 'Physical School' },
        { id: 'Pregnancy Test', name: 'Pregnancy Test' },
        { id: 'Rash', name: 'Rash' },
        { id: 'Refill', name: 'Refill' },
        { id: 'Respiratory Disorder', name: 'Respiratory Disorder' },
        { id: 'Result', name: 'Result' },
        { id: 'Semaglutide', name: 'Semaglutide' },
        { id: 'Shoulder pain', name: 'Shoulder pain' },
        { id: 'Skin Lesion', name: 'Skin Lesion' },
        { id: 'Sore Throat', name: 'Sore Throat' },
        { id: 'Stomachache', name: 'Stomachache' },
        { id: 'USG', name: 'USG' },
        { id: 'Vitamin pack', name: 'Vitamin pack' },
        { id: 'X Ray', name: 'X Ray' },
      ],
      normalizer(node) {
        return {
          id: node.id,
          label: node.name,
        }
      },
    }
  },
  setup(props, { emit }) {
    const stateDataTem = {
      name: '',
      lastname: '',
      phone: '',
      date_birth: '',
      cupon: '',
      type_visits_id: null,
      gender: null,
      codeQr: null,
      user_references_id: null,
      email: '',
      address: '',
      cities_id: null,
      zip_codes_id: null,
      reason_visit: null,
      reason_visit_other: '',
    }

    const toast = ref(null)
    const loading = ref(false)
    const inputScan = ref(null)
    const checkCodeQr = ref(false)
    const optionsTypeVisit = ref([])
    const clearTimeoutBuscador = ref('')
    const optionsReferenceList = ref([])
    const cityName = ref('')
    const cityOptions = ref([])
    const setTimeoutCitySearch = ref(null)
    const isAddNewConsultSidebarActive = ref(false)
    const stateData = ref(JSON.parse(JSON.stringify(stateDataTem)))
    const membershipData = ref(null)

    const searchCitiesFromInput = () => {
      if (!cityName.value || !cityName.value.length) {
        cityOptions.value = []
        return
      }
      clearTimeout(setTimeoutCitySearch.value)
      setTimeoutCitySearch.value = setTimeout(() => {
        axiosC
          .citiesFilterFilters({ name: cityName.value })
          .then((response) => {
            const list = Array.isArray(response) ? response : (response && response.data) || []
            cityOptions.value = (list || []).map(city => ({
              text: city.name,
              value: city.id,
            }))
          })
          .catch(() => {
            cityOptions.value = []
          })
      }, 350)
    }

    const onCitySelect = () => {
      const selected = cityOptions.value.find(opt => opt.value === stateData.value.cities_id)
      if (selected) cityName.value = selected.text
    }

    const selectCityOption = (opt) => {
      stateData.value.cities_id = opt.value
      cityName.value = opt.text
      cityOptions.value = []
    }

    const resetuserData = () => {
      stateData.value = JSON.parse(JSON.stringify(stateDataTem))
      cityName.value = ''
      cityOptions.value = []
    }

    const cancelCupon = () => {
      stateData.value.cupon = ''
    }

    const clearOtherReason = () => {
      if (stateData.value.reason_visit !== 'Other') {
        stateData.value.reason_visit_other = ''
      }
    }

    watch(
      () => stateData.value.codeQr,
      newValue => {
        if (newValue !== null || newValue.length > 0) {
          const data = { codeQr: stateData.value.codeQr }
          axiosA.appointmentAssignCuppon(data).then(res => {
            const { type, message } = notificationCheck(res)
            toast.value[type](message)
          })
        }
      }
    )

    watch(
      () => stateData.value.reason_visit,
      () => {
        clearOtherReason()
      }
    )

    const showConditionalContent = computed(() => {
      return stateData.value.type_visits_id === 8
    })

    const showConditionalContentRef = computed(() => {
      return stateData.value.type_visits_id === 2 || stateData.value.type_visits_id === 3 || stateData.value.type_visits_id === 13 || stateData.value.type_visits_id === 14
    })

    const showOtherReasonInput = computed(() => {
      return stateData.value.reason_visit === 'Other'
    })

    const scanPromotion = () => {
      if (checkCodeQr.value) {
        stateData.value.codeQr = null
      }
      checkCodeQr.value = !checkCodeQr.value
    }

    const formatPhone = computed({
      get() {
        let { phone } = stateData.value
        phone = phone.toString()
        const match = phone
          .replace(/\D+/g, '')
          .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        return match
      },
      set(val) {
        stateData.value.phone = val
      },
    })

    /**
     * Filtra los tipos de visita para excluir los relacionados con insurance
     * Excluye tipos que contengan "insurance" en el nombre
     */
    const filterNonInsuranceVisitTypes = visitTypes => {
      if (!Array.isArray(visitTypes)) return []
      return visitTypes.filter(visit => {
        const name = (visit.name || '').toString().toLowerCase()
        // Excluir tipos que contengan "insurance"
        return !name.includes('insurance')
      })
    }

    const createConsult = () => {
      loadTypeVisit()
      isAddNewConsultSidebarActive.value = false
    }

    const searchTypeVisit = (name, loading) => {
      if (name.length) {
        const datos = { name: name, status: true }
        loading(true)
        clearTimeout(clearTimeoutBuscador.value)
        clearTimeoutBuscador.value = setTimeout(() => {
          axiosTV.visitFilter(20, datos).then(({ data }) => {
            loading(false)
            // Excluir tipos de visita relacionados con insurance
            optionsTypeVisit.value = filterNonInsuranceVisitTypes(data)
          })
        }, 400)
      }
    }

    onMounted(() => {
      loadTypeVisit()
      loadReferencesList()
    })

    const loadTypeVisit = () => {
      axiosTV.visitList(20).then(({ data }) => {
        // Excluir tipos de visita relacionados con insurance
        optionsTypeVisit.value = filterNonInsuranceVisitTypes(data)
      })
    }

    const loadReferencesList = () => {
      axiosP.referencePagination(10, 1).then(({ data }) => {
        optionsReferenceList.value = data.data
      })
    }

    const handleDataMemberchip = dato => {
      membershipData.value = dato
    }

    const onSubmit = async () => {
      loading.value = true
      const phone = stateData.value.phone.replace(/[() -]/g, '')
      
      // Determine the final reason_visit value
      let finalReasonVisit = stateData.value.reason_visit
      if (stateData.value.reason_visit === 'Other' && stateData.value.reason_visit_other) {
        finalReasonVisit = stateData.value.reason_visit_other
      }
      
      // Create a copy of stateData without reason_visit_other
      const { reason_visit_other, ...stateDataWithoutOther } = stateData.value
      
      const datos = {
        ...stateDataWithoutOther,
        phone,
        reason_visit: finalReasonVisit,
        ...membershipData.value,
        type_membership: 'NP',
      }
      await axiosP
        .patientCreate(datos)
        .then(res => {
          const { type, message } = notificationCheck(res)
          const patientId =
            res?.data?.id ?? res?.id ?? res?.registro?.id ?? res?.data?.user?.id ?? null
          emit('createPatient', { type, message, patientId, patient: res?.data ?? res })
          loading.value = false
          resetuserData()
        })
        .catch(error => {
          loading.value = false
          const { type, message } = notificationCheck(error.response)
          emit('createPatient', { type, message })
        })
    }

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetuserData)

    return {
      loading,
      stateData,
      formatPhone,
      toast,
      checkCodeQr,
      inputScan,
      scanPromotion,
      onSubmit,
      isAddNewConsultSidebarActive,
      createConsult,
      optionsTypeVisit,
      searchTypeVisit,
      optionsReferenceList,
      cityName,
      cityOptions,
      searchCitiesFromInput,
      onCitySelect,
      selectCityOption,
      refFormObserver,
      getValidationState,
      resetForm,
      config: {
        dateFormat: 'm-d-Y',
        allowInput: true,
        static: true,
      },
      cancelCupon,
      showConditionalContent,
      showConditionalContentRef,
      showOtherReasonInput,
      handleDataMemberchip,
      membershipData,
    }
  },
}
</script>

<style lang="scss" scoped>
.button-custom {
  padding: 5px !important;
}

.city-autocomplete-wrap {
  position: relative;
}

.city-options-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
}

.city-option-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}

.city-option-item:hover {
  background-color: #f8f9fa;
}

.city-option-item:last-child {
  border-bottom: none;
}

.flatpickr-wrapper {
  width: 100% !important;
}

.media-body {
  align-self: center;
}
.is-invalid {
  border-color: red;
}

.custom-treeselect {
  :deep(.vue-treeselect__control) {
    border-radius: 8px;
    border: 2px solid #e1e5e9;
    transition: all 0.3s ease;
    font-size: 14px;
    padding: 8px 12px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    min-height: 48px;
    
    &:hover {
      border-color: #007bff;
    }
  }
  
  :deep(.vue-treeselect__control--focused) {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }
  
  :deep(.vue-treeselect__placeholder) {
    color: #6c757d;
    font-style: italic;
  }
  
  :deep(.vue-treeselect__menu) {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e1e5e9;
    max-height: 200px;
    overflow-y: auto;
  }
  
  :deep(.vue-treeselect__option) {
    padding: 10px 16px;
    font-size: 14px;
    line-height: 1.4;
    transition: background-color 0.2s ease;
  }
  
  :deep(.vue-treeselect__option:hover) {
    background-color: #f8f9fa;
  }
  
  :deep(.vue-treeselect__option--selected) {
    background-color: #007bff;
    color: white;
  }
  
  :deep(.vue-treeselect__input) {
    font-size: 14px;
    padding: 4px 0;
  }
  
  :deep(.vue-treeselect__single-value) {
    font-size: 14px;
    color: #495057;
  }
}

// Estilo para el treeselect cuando está en estado de error
.custom-treeselect.is-invalid {
  :deep(.vue-treeselect__control) {
    border-color: #dc3545;
  }
  
  :deep(.vue-treeselect__control--focused) {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
}

// Estilo para el treeselect cuando está en estado válido
.custom-treeselect.is-valid {
  :deep(.vue-treeselect__control) {
    border-color: #28a745;
  }
  
  :deep(.vue-treeselect__control--focused) {
    border-color: #28a745;
    box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
  }
}
</style>
